<script setup lang="ts">
import AirdropActionBtn from './components/AirdropActionBtn.vue';
import AirdropInfo from './components/AirdropInfo.vue';
import AirdropName from './components/AirdropName.vue';
import AirdropStatusChip from './components/AirdropStatusChip.vue';
import BaseAddress from '@/components/molecules/BaseAddress.vue';
import BaseAmountToken from '@/components/BaseAmountToken.vue';
import BaseAsset from '@/components/BaseAsset.vue';
import BaseAssetFromAddress from '@/components/BaseAssetFromAddress.vue';
import BaseValue from '@/components/BaseValue.vue';
import { AirdropViewMode } from '@/types';
import { isEligibleAmount } from '@sonarwatch/portfolio-core';
import useAirdropChecker from './useAirdropChecker';
import { inject, provide } from 'vue';

const airdropCheckerData = useAirdropChecker();
provide('useAirdropChecker', airdropCheckerData);
const {
  mode,
  page,
  pageNext,
  pagePrevious,
  addressViewItems,
  selectAddressViewItems,
  selectAirdropViewItems,
  fetcherFailedIds,
  selectedAddress,
  selectedAirdrop,
  toogleViewMode,
  results,
  cellClass,
  airdropViewItems,
  selectedAirdropInfo,
  isCheckDone,
} = inject('useAirdropChecker') as ReturnType<typeof useAirdropChecker>;
</script>

<template>
  <div v-if="isCheckDone" class="flex flex-col gap-4 pb-8">
    <v-divider class="my-10"></v-divider>
    <div class="d-flex flex-wrap align-center">
      <v-select
        v-show="mode === AirdropViewMode.address"
        v-model="selectedAddress"
        max-width="560px"
        density="compact"
        placeholder="Select address"
        :items="selectAddressViewItems"
        variant="solo-filled"
        flat
        :transition="false"
        hide-details
        :disabled="results.length === 0"
        :list-props="{
          density: 'compact',
        }"
      ></v-select>
      <v-select
        v-show="mode === AirdropViewMode.aidrop"
        v-model="selectedAirdrop"
        max-width="300px"
        density="compact"
        placeholder="Select airdrop"
        :items="selectAirdropViewItems"
        variant="solo-filled"
        flat
        eager
        :transition="false"
        hide-details
        :disabled="results.length === 0"
        :list-props="{
          density: 'compact',
        }"
      >
        <template #item="{ item, props }">
          <v-list-item v-bind="props" :title="undefined" density="compact">
            <AirdropName
              :emitterName="item.raw.emitterName"
              :name="item.raw.name"
              :image="item.raw.image"
              showImage
            ></AirdropName>
          </v-list-item>
        </template>
        <template #selection="{ item }">
          <v-list-item density="compact" class="mx-0 pa-0 my-n2">
            <AirdropName
              :emitterName="item.raw.emitterName"
              :name="item.raw.name"
              :image="item.raw.image"
              showImage
            ></AirdropName>
          </v-list-item>
        </template>
      </v-select>
      <div class="d-flex align-center" v-if="results.length !== 0">
        <v-btn
          icon="mdi-chevron-left"
          size="small"
          rounded
          flat
          variant="plain"
          @click="pagePrevious"
        >
        </v-btn>
        <div class="text-caption text-medium-emphasis mx-2 text-no-wrap">
          {{ page }} /
          {{
            mode === AirdropViewMode.address
              ? results.length
              : selectAirdropViewItems.length
          }}
        </div>
        <v-btn
          icon="mdi-chevron-right"
          size="small"
          rounded
          flat
          variant="plain"
          @click="pageNext"
        ></v-btn>
      </div>
      <v-spacer></v-spacer>
      <v-btn @click="toogleViewMode">
        <v-icon class="mr-2">mdi-table-pivot</v-icon>
        Switch to
        {{
          mode === AirdropViewMode.address ? 'airdrop mode' : 'address mode'
        }}</v-btn
      >
    </div>

    <!-- Address View -->
    <v-data-table
      v-show="mode === AirdropViewMode.address"
      items-per-page="1000"
      class="mt-2 rounded-lg border"
      :items="addressViewItems"
      :mobile="false"
      item-value="id"
      :headers="[
        {
          title: 'Platform',
          align: 'start',
          sortable: false,
          key: 'platformId',
        },
        {
          title: 'Asset',
          align: 'start',
          sortable: false,
          key: 'asset',
        },
        {
          title: 'Amount',
          align: 'end',
          sortable: false,
          key: 'amount',
        },
        {
          title: 'Action',
          align: 'end',
          sortable: false,
          key: 'action',
        },
      ]"
    >
      <template #bottom></template>
      <!-- eslint-disable vue/valid-v-slot -->
      <template #item.platformId="{ item }">
        <div class="d-flex align-center text-no-wrap">
          <AirdropName
            :emitterName="item.emitterName"
            :name="item.name"
            :image="item.image"
          ></AirdropName>
          <AirdropInfo
            :claim-start="item.claimStart"
            :claim-end="item.claimEnd"
          ></AirdropInfo>
        </div>
      </template>
      <template #item.asset="{ item: airdrop }">
        <div
          v-for="aItem in airdrop.items"
          :key="`${airdrop.owner}-${airdrop.id}-${aItem.label}`"
          :class="cellClass('d-flex align-center')"
        >
          <div style="min-width: 95px">
            <BaseAssetFromAddress
              v-if="aItem.address"
              class="mr-2"
              :address="aItem.address"
              :networkId="airdrop.networkId"
            ></BaseAssetFromAddress>
            <BaseAsset
              v-else
              :images="aItem.imageUri ? [aItem.imageUri] : undefined"
              :label="aItem.label"
            ></BaseAsset>
          </div>
          <AirdropStatusChip
            class="mr-2"
            :airdropStatus="airdrop.status"
            :claimStart="airdrop.claimStart"
            :claimEnd="airdrop.claimEnd"
            :status="aItem.status"
          ></AirdropStatusChip>
        </div>
      </template>
      <template #item.amount="{ item: airdrop }">
        <div
          style="min-width: 130px"
          v-for="(aItem, index) in airdrop.items"
          :key="aItem.address || index"
        >
          <div :class="cellClass('justify-end')">
            <div
              v-if="isEligibleAmount(aItem.amount)"
              class="d-flex flex-wrap align-center"
            >
              <div>
                <BaseValue
                  class="text-caption text-medium-emphasis"
                  :value="aItem.value"
                />
              </div>
              <BaseAmountToken
                class="ml-2"
                :config="{
                  amount: aItem.amount,
                  label: aItem.label,
                }"
              ></BaseAmountToken>
            </div>
          </div>
        </div>
      </template>
      <template #item.action="{ item: airdrop }">
        <div
          v-for="aItem in airdrop.items"
          :key="aItem.address || aItem.label"
          :class="cellClass()"
        >
          <AirdropActionBtn
            :airdropStatus="airdrop.status"
            :item="aItem"
            :claimLink="airdrop.claimLink || airdrop.emitterLink"
          ></AirdropActionBtn>
        </div>
      </template>
    </v-data-table>

    <!-- Airdrop View -->
    <v-data-table
      v-show="mode === AirdropViewMode.aidrop"
      class="mt-2 rounded-lg border"
      items-per-page="1000"
      :items="airdropViewItems"
      item-value="owner"
      :mobile="false"
      :headers="[
        {
          title: 'Address',
          align: 'start',
          sortable: false,
          key: 'owner',
        },
        {
          title: 'Asset',
          align: 'start',
          sortable: false,
          key: 'asset',
        },
        {
          title: 'Amount',
          align: 'end',
          sortable: false,
          key: 'amount',
        },
        {
          title: 'Action',
          align: 'end',
          sortable: false,
          key: 'action',
        },
      ]"
    >
      <template #bottom></template>
      <template #header.asset="{}">
        <span>Asset</span>
        <AirdropInfo
          :claimStart="selectedAirdropInfo.claimStart"
          :claimEnd="selectedAirdropInfo.claimEnd"
        ></AirdropInfo>
      </template>
      <!-- eslint-disable vue/valid-v-slot -->
      <template #item.owner="{ item: airdrop }">
        <BaseAddress
          :address="airdrop.owner"
          forceShort
          click-to-copy
        ></BaseAddress>
      </template>
      <template #item.asset="{ item: airdrop }">
        <div
          v-for="aItem in airdrop.items"
          :key="`${airdrop.owner}-${airdrop.id}-${aItem.label}`"
          :class="cellClass(`d-flex align-center my-2`)"
        >
          <div style="min-width: 95px">
            <BaseAssetFromAddress
              v-if="aItem.address"
              class="mr-2"
              :address="aItem.address"
              :networkId="airdrop.networkId"
            ></BaseAssetFromAddress>
            <BaseAsset
              v-else
              :images="aItem.imageUri ? [aItem.imageUri] : undefined"
              :label="aItem.label"
            ></BaseAsset>
          </div>
          <AirdropStatusChip
            class="mr-2"
            :airdropStatus="airdrop.status"
            :claimStart="airdrop.claimStart"
            :claimEnd="airdrop.claimEnd"
            :status="aItem.status"
          ></AirdropStatusChip>
        </div>
      </template>
      <template #item.amount="{ item: airdrop }">
        <div
          style="min-width: 130px"
          v-for="(aItem, index) in airdrop.items"
          :key="aItem.address || index"
        >
          <div :class="cellClass('justify-end')">
            <div
              class="d-flex align-center flex-nowrap"
              v-if="isEligibleAmount(aItem.amount)"
            >
              <div>
                <BaseValue
                  class="text-caption text-medium-emphasis"
                  :value="aItem.value"
                />
              </div>
              <BaseAmountToken
                class="ml-2"
                :config="{
                  amount: aItem.amount,
                  label: aItem.label,
                }"
              ></BaseAmountToken>
            </div>
          </div>
        </div>
      </template>
      <template #item.action="{ item: airdrop }">
        <div
          v-for="aItem in airdrop.items"
          :key="aItem.address || aItem.label"
          :class="cellClass()"
        >
          <AirdropActionBtn
            :airdropStatus="airdrop.status"
            :item="aItem"
            :claimLink="airdrop.claimLink || airdrop.emitterLink"
          ></AirdropActionBtn>
        </div>
      </template>
    </v-data-table>

    <div
      class="text-disabled -mt-2 flex items-center gap-2 bg-white/5 p-4 rounded-lg"
      v-if="fetcherFailedIds.length > 0"
    >
      <v-icon size="x-small" icon="mdi-alert-circle"></v-icon>
      <p class="text-sm">
        Failed to fetch {{ fetcherFailedIds.join(', and ') }}.<br />If the
        problem persists, please
        <a
          href="https://discord.com/invite/gG4DvM2JGw"
          target="_blank"
          class="!underline"
          >contact the team</a
        >.
      </p>
    </div>

    <div
      class="text-disabled mt-2 max-w-[700px] self-center w-full text-center text-sm"
    >
      The Airdrop Checker may contain calculation errors or inaccuracies. Always
      verify your token allocations on the official sites for the most reliable
      information
    </div>
  </div>
</template>
